<script setup>
import { useRegisterSW } from "virtual:pwa-register/vue";
import { markRaw, onMounted, ref } from "vue";

// --- STATE ---
const canInstall = ref(false);
const showPrompt = ref(false);
const isIOS = ref(false);
const isDialogVisible = ref(false);
const deferredPrompt = ref(null);

// YENİ: Güncelleme işlemi başladığında dönecek yükleme durumu
const isUpdating = ref(false);

// --- PWA HOOK ---
const { needRefresh, updateServiceWorker } = useRegisterSW({
  onRegisteredSW(swUrl, r) {
    r &&
      setInterval(() => {
        r.update();
      }, 60 * 60 * 1000); // 1 saatlik kontrol
  },
});

const closeUpdateSnackBar = () => {
  needRefresh.value = false;
};

// --- YENİ: GÜNCELLEME FONKSİYONU ---
const performUpdate = async () => {
  // 1. Yükleme ekranını aç
  isUpdating.value = true;

  // 2. Kullanıcı görsün diye çok kısa (0.5sn) beklet (Opsiyonel ama şık durur)
  await new Promise((resolve) => setTimeout(resolve, 500));

  // 3. Service Worker'ı güncelle (Bu işlem sayfayı yeniler)
  await updateServiceWorker();
};

// --- DİĞER FONKSİYONLAR (Aynen kaldı) ---
const isDismissedRecently = () => {
  const dismissedTimestamp = localStorage.getItem("pwa-prompt-dismissed-time");
  if (!dismissedTimestamp) return false;
  const now = Date.now();
  const fifteenDaysInMs = 15 * 24 * 60 * 60 * 1000;
  if (now - parseInt(dismissedTimestamp) < fifteenDaysInMs) return true;
  localStorage.removeItem("pwa-prompt-dismissed-time");
  return false;
};

const dismissPrompt = () => {
  showPrompt.value = false;
  isDialogVisible.value = false;
  const now = Date.now();
  localStorage.setItem("pwa-prompt-dismissed-time", now.toString());
};

const installPwa = async () => {
  if (!deferredPrompt.value) return;
  try {
    await deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    if (outcome === "accepted") {
      showPrompt.value = false;
      isDialogVisible.value = false;
    }
    deferredPrompt.value = null;
    canInstall.value = false;
  } catch (err) {
    console.error("Yükleme hatası:", err);
  }
};

// --- LIFECYCLE ---
onMounted(() => {
  if (window.matchMedia("(display-mode: standalone)").matches) {
    showPrompt.value = false;
    return;
  }
  if (isDismissedRecently()) return;

  const userAgent = window.navigator.userAgent.toLowerCase();
  isIOS.value = /iphone|ipad|ipod/.test(userAgent);

  if (isIOS.value) {
    setTimeout(() => {
      showPrompt.value = true;
    }, 3000);
  }

  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt.value = markRaw(e);
    canInstall.value = true;
    if (!isDismissedRecently()) showPrompt.value = true;
  });
});
</script>

<template>
  <div v-if="showPrompt" class="d-flex justify-center my-4">
    <VBtn
      prepend-icon="tabler-download"
      variant="tonal"
      color="primary"
      @click="isDialogVisible = true"
    >
      Uygulamayı Yükle
    </VBtn>
  </div>

  <VSnackbar
    v-model="needRefresh"
    location="bottom right"
    variant="flat"
    color="surface"
    class="text-high-emphasis"
    :timeout="-1"
    elevation="4"
    z-index="9000"
  >
    <div class="d-flex align-center gap-2">
      <VIcon icon="tabler-cloud-download" color="primary" />
      <div>
        <span class="font-weight-bold d-block">Güncelleme Mevcut</span>
        <span class="text-caption text-medium-emphasis"
          >Yeni özellikler yüklendi.</span
        >
      </div>
    </div>

    <template #actions>
      <VBtn
        color="primary"
        variant="elevated"
        size="small"
        @click="performUpdate"
      >
        Yenile
      </VBtn>
      <VBtn
        color="secondary"
        variant="text"
        size="small"
        icon="tabler-x"
        @click="closeUpdateSnackBar"
      />
    </template>
  </VSnackbar>

  <VOverlay
    v-model="isUpdating"
    class="align-center justify-center text-center"
    persistent
    z-index="9999"
    opacity="0.9"
    scrim="#ffffff"
  >
    <div class="d-flex flex-column align-center">
      <VProgressCircular
        indeterminate
        color="primary"
        size="64"
        width="6"
        class="mb-4"
      />

      <h3 class="text-h5 font-weight-bold text-primary mb-1">
        Sistem Güncelleniyor
      </h3>
      <p class="text-body-1 text-medium-emphasis">
        Lütfen bekleyin, en güncel sürüm yükleniyor...
      </p>
    </div>
  </VOverlay>

  <VDialog v-model="isDialogVisible" max-width="500" persistent>
    <VCard class="pa-4 rounded-lg">
      <div class="d-flex justify-space-between align-center mb-4">
        <h3 class="text-h6 font-weight-bold">Uygulamayı Yükle</h3>
        <VBtn
          icon="tabler-x"
          variant="text"
          size="small"
          @click="isDialogVisible = false"
        />
      </div>
      <div class="d-flex align-center mb-4">
        <img
          src="/favicon-96x96.png"
          alt="App Icon"
          class="rounded me-4"
          width="64"
          height="64"
          style="background-color: #f0f0f0; object-fit: contain"
        />
        <div>
          <p class="text-body-1 mb-1 font-weight-medium">SporAsistan'ı</p>
          <p class="text-caption text-medium-emphasis">
            Daha hızlı erişim ve tam ekran deneyimi için ana ekrana ekleyin.
          </p>
        </div>
      </div>
      <VDivider class="mb-4" />
      <div v-if="canInstall">
        <div class="d-flex gap-2 justify-end">
          <VBtn variant="text" color="secondary" @click="dismissPrompt"
            >15 Gün Sorma</VBtn
          >
          <VBtn
            color="primary"
            prepend-icon="tabler-device-mobile-down"
            @click="installPwa"
            >Yükle</VBtn
          >
        </div>
      </div>
      <div v-else-if="isIOS">
        <VAlert
          type="info"
          variant="tonal"
          border="start"
          class="mb-0 text-caption"
        >
          <div class="d-flex align-center flex-wrap">
            <span>Tarayıcı menüsündeki</span>
            <VIcon icon="tabler-share" size="small" class="mx-1" />
            <strong>Paylaş</strong> ikonuna basın ve
            <br />
            <strong>"Ana Ekrana Ekle"</strong> seçeneğini seçin.
          </div>
        </VAlert>
        <div class="d-flex justify-end mt-3">
          <VBtn variant="text" color="primary" @click="dismissPrompt">
            Anladım
          </VBtn>
        </div>
      </div>
      <div v-else class="text-center">
        <p class="text-caption">
          Cihazınız otomatik yüklemeyi desteklemiyor olabilir. Tarayıcı
          ayarlarını kontrol ediniz.
        </p>
        <VBtn
          class="mt-2"
          variant="text"
          color="primary"
          @click="isDialogVisible = false"
          >Kapat</VBtn
        >
      </div>
    </VCard>
  </VDialog>
</template>
